import React, { ReactElement, useEffect, useState, useMemo, useRef } from 'react';
import { Autocomplete, TextField, Typography } from '@mui/material';
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import moment from 'moment';
import { ThunkDispatch } from 'redux-thunk';

import { matchLearners } from 'store/learner/actions';
import { matchedLearnersSelector } from 'store/learner/selectors';
import { AppState } from 'store';
import { ILearnerMatchingModel, IMatchedLearner, IValidateLearnersToolFormModel, PARSAction } from 'core/models';
import { escapeRegexCharacters } from 'utils';

export interface LearnerAutocompleteInputProps {
  name: keyof IValidateLearnersToolFormModel;
  label?: string;
  placeholder?: string;
  formValues: IValidateLearnersToolFormModel;
  onSelect: (learner: IMatchedLearner) => void;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
}

export interface IMatchedLearnerOption extends IMatchedLearner {
  matchedOption?: {
    abbreviation: string;
    learnerId?: string;
    name: string;
  };
}

const formatOptionLabel = (option: IMatchedLearnerOption): string => {
  const stateAbbr = option.matchedOption?.abbreviation || '';
  const licenseId = option.matchedOption?.learnerId || '';

  let label = `${option.firstName} ${option.lastName}`;
  if (stateAbbr || licenseId) {
    label += `   ${stateAbbr} - ${licenseId}`;
  }
  if (option.npi) {
    label += ` | NPI: ${option.npi}`;
  }
  return label;
};

const highlightMatch = (text: string, queries: string[]): React.ReactNode => {
  if (!text || queries.length === 0) return text;

  // Filter out empty queries and escape special regex characters
  const validQueries = queries.filter((q) => q && q.length > 0).map((q) => escapeRegexCharacters(q));

  if (validQueries.length === 0) return text;

  // Create a combined regex pattern for all queries
  const regex = new RegExp(`(${validQueries.join('|')})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, index) => {
    // Check if this part matches any of the queries (case-insensitive)
    const isMatch = validQueries.some((query) => part.toLowerCase() === query.toLowerCase());
    if (isMatch) {
      return <strong key={index}>{part}</strong>;
    }
    return part;
  });
};

export const LearnerAutocompleteInput = ({
  name,
  label,
  placeholder,
  formValues,
  onSelect,
  helperText,
  required,
  disabled,
}: LearnerAutocompleteInputProps): ReactElement => {
  const dispatch: ThunkDispatch<AppState, unknown, PARSAction> = useDispatch();
  const { setFieldValue, values } = useFormikContext<IValidateLearnersToolFormModel>();

  const matchedLearners = useSelector(matchedLearnersSelector);

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState((values[name] as string) || '');
  const formValuesRef = useRef(formValues);

  const options = useMemo(() => {
    if (!matchedLearners) return [];

    const matches = matchedLearners
      .flatMap((learner): IMatchedLearnerOption[] => {
        if (learner.learnerStateBoards || learner.learnerMocBoards) {
          const stateBoardSet = new Set<string>();
          const uniqueStateBoards = learner.learnerStateBoards.filter((board) => {
            const key = `${board.abbreviation}-${board.learnerId}`;
            if (stateBoardSet.has(key)) return false;
            stateBoardSet.add(key);
            return true;
          });

          const matchedLearnerOptions: IMatchedLearnerOption[] = [];
          matchedLearnerOptions.push(
            ...uniqueStateBoards.map((stateBoard) => ({
              ...learner,
              matchedOption: {
                abbreviation: stateBoard.abbreviation || '',
                learnerId: stateBoard.learnerId || '',
                name: stateBoard.name || '',
              },
            })),
          );

          return matchedLearnerOptions;
        }

        return [learner];
      })
      .slice(0, 10);

    return matches;
  }, [matchedLearners]);

  useEffect(() => {
    setInputValue((values[name] as string) || '');
  }, [values, name]);

  useEffect(() => {
    formValuesRef.current = formValues;
  }, [formValues]);

  const searchLearners = useMemo(
    () =>
      debounce((input: string, fieldName: keyof IValidateLearnersToolFormModel) => {
        const currentFormValues = formValuesRef.current;
        if (!input || input.length < 2) return;

        const { dateOfBirth, licenseId } = currentFormValues;

        const effectiveLicenseId = fieldName === 'licenseId' ? input : licenseId;

        const isValidDate = dateOfBirth && !isNaN(new Date(dateOfBirth).getTime());
        const dobMoment = isValidDate ? moment(dateOfBirth) : null;
        const birthMonth = dobMoment ? dobMoment.month() + 1 : 0;
        const birthDay = dobMoment ? dobMoment.date() : 0;

        const payload: ILearnerMatchingModel = {
          firstName: fieldName === 'firstName' ? input : currentFormValues.firstName || '',
          lastName: fieldName === 'lastName' ? input : currentFormValues.lastName || '',
          npi: fieldName === 'npi' ? input : currentFormValues.npi || '',
          licenseId: effectiveLicenseId,
          stateName: currentFormValues.stateName?.label,
          birthDay,
          birthMonth,
        };

        if (currentFormValues.licenseId && !currentFormValues.stateName) return;

        dispatch(matchLearners(payload));
      }, 500),
    [dispatch],
  );

  useEffect(() => {
    if (open && inputValue?.length > 1) {
      searchLearners(inputValue, name);
    }
  }, [inputValue, searchLearners, name, open]);

  const handleInputChange = (_event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
    if (!open && newInputValue.length > 1) {
      setOpen(true);
    }
    setFieldValue(name, newInputValue);
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: IMatchedLearnerOption | string | null) => {
    if (typeof newValue === 'object' && newValue !== null) {
      onSelect(newValue);
      setOpen(false);
    }
  };

  return (
    <Autocomplete<IMatchedLearnerOption, false, false, true>
      id={`learner-autocomplete-${name}`}
      open={open}
      onOpen={() => inputValue.length > 1 && setOpen(true)}
      onClose={() => setOpen(false)}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      isOptionEqualToValue={(option, value) => {
        if (typeof option === 'string' || typeof value === 'string') return option === value;
        return option.npi === value.npi && option.firstName === value.firstName && option.lastName === value.lastName;
      }}
      getOptionLabel={(option) => (typeof option === 'string' ? option : formatOptionLabel(option))}
      options={options}
      freeSolo
      renderOption={(props, option) => {
        const optionText = typeof option === 'string' ? option : formatOptionLabel(option);
        // Collect all typed values from the form
        const searchTerms = [
          values.firstName,
          values.lastName,
          values.licenseId,
          values.npi,
          values.stateName?.id,
        ].filter((v) => v && typeof v === 'string' && v.length > 0) as string[];

        return (
          <li {...props}>
            <Typography variant="body2" color="textSecondary">
              {highlightMatch(optionText, searchTerms)}
            </Typography>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          required={required}
          helperText={helperText}
          disabled={disabled}
        />
      )}
    />
  );
};
